import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { UsageBar } from '../components/UsageBar';
import { GlassCard } from '../components/GlassCard';
import { Toast } from '../components/Toast';
import { api, ApiError } from '../lib/api';
import { SettingsCardSkeleton } from '../components/Skeleton';
import { PhoneVerifyModal } from '../components/PhoneVerifyModal';
import { T } from '../lib/design-tokens';
import type { SubscriptionRecord } from '../types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

interface SubscriptionCurrentResponse {
  subscription: SubscriptionRecord | null;
  usage: {
    voice: { allowed: boolean; used: number; limit: number; tier: string };
    email: { allowed: boolean; used: number; limit: number; tier: string };
  };
  profile: {
    email: string;
    phoneVerified: boolean;
    gmailConnected: boolean;
  };
}

export function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sub, setSub] = useState<SubscriptionRecord | null>(null);
  const [usage, setUsage] = useState<SubscriptionCurrentResponse['usage'] | null>(null);
  const [profile, setProfile] = useState<SubscriptionCurrentResponse['profile'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<string | null>(null);
  const [checkingOut, setCheckingOut] = useState<string | null>(null);
  const [managingBilling, setManagingBilling] = useState(false);
  const [showPhoneVerify, setShowPhoneVerify] = useState(false);

  useEffect(() => {
    api<SubscriptionCurrentResponse>('/subscriptions/current')
      .then((data) => {
        setSub(data.subscription);
        setUsage(data.usage);
        setProfile(data.profile);
      })
      .catch(() => setToast('Failed to load subscription info'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const gmailParam = searchParams.get('gmail');
    if (gmailParam === 'connected') {
      setToast('Gmail connected successfully');
      api<SubscriptionCurrentResponse>('/subscriptions/current')
        .then((data) => {
          setProfile(data.profile);
          setSub(data.subscription);
          setUsage(data.usage);
        })
        .catch(() => {});
      searchParams.delete('gmail');
      setSearchParams(searchParams, { replace: true });
    } else if (gmailParam === 'error') {
      setToast('Failed to connect Gmail. Please try again.');
      searchParams.delete('gmail');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const tier = sub?.tier ?? 'starter';
  const tierLabel = tier.charAt(0).toUpperCase() + tier.slice(1);

  const handleManage = async () => {
    setManagingBilling(true);
    try {
      const data = await api<{ portalUrl: string }>('/subscriptions/portal', { method: 'POST' });
      window.location.href = data.portalUrl;
    } catch (err) {
      setToast(err instanceof ApiError ? err.message : 'Failed to open billing portal');
      setManagingBilling(false);
    }
  };

  const handleUpgrade = async (targetTier: 'pro' | 'max') => {
    setCheckingOut(targetTier);
    try {
      const data = await api<{ checkoutUrl: string }>('/subscriptions/checkout', {
        method: 'POST',
        body: JSON.stringify({ tier: targetTier }),
      });
      window.location.href = data.checkoutUrl;
    } catch (err) {
      setToast(err instanceof ApiError ? err.message : 'Failed to start checkout');
      setCheckingOut(null);
    }
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontFamily: T.mono,
    fontSize: 10,
    fontWeight: 600,
    color: T.cream20,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    marginBottom: 16,
  };

  return (
    <div className="max-w-[760px] mx-auto" style={{ padding: '40px 40px' }}>
      <h1
        style={{
          fontFamily: T.serif,
          fontSize: 30,
          fontWeight: 400,
          color: T.cream,
          marginBottom: 32,
          letterSpacing: '-0.5px',
          animation: 'fadeUp 0.5s ease both',
        }}
      >
        Settings
      </h1>

      {loading && (
        <div className="flex flex-col gap-5">
          {[0, 1, 2].map((i) => (
            <SettingsCardSkeleton key={i} />
          ))}
        </div>
      )}

      {!loading && (
        <div className="flex flex-col gap-5">
          <GlassCard style={{ padding: '24px 28px', animation: 'fadeUp 0.4s ease 0.1s both' }}>
            <div style={sectionTitleStyle}>Subscription</div>
            <div className="flex justify-between items-center">
              <div>
                <div
                  style={{
                    fontFamily: T.mono,
                    fontSize: 22,
                    fontWeight: 700,
                    color: T.gold,
                    marginBottom: 4,
                  }}
                >
                  {tier === 'starter' ? 'FREE' : tierLabel}
                </div>
                <div style={{ fontFamily: T.sans, fontSize: 13, color: T.cream35 }}>
                  {tier === 'starter'
                    ? 'Upgrade for more calls and emails'
                    : (usage?.voice.limit ?? 9999) < 9999 || (usage?.email.limit ?? 9999) < 9999
                      ? 'View usage below'
                      : 'Unlimited operations'}
                </div>
              </div>
              {tier !== 'starter' && (
                <button
                  type="button"
                  onClick={handleManage}
                  disabled={managingBilling}
                  style={{
                    padding: '8px 18px',
                    borderRadius: 8,
                    border: `1px solid ${T.cream08}`,
                    background: 'transparent',
                    color: T.cream35,
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: managingBilling ? 'default' : 'pointer',
                    fontFamily: T.mono,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                  }}
                >
                  {managingBilling ? 'Loading...' : 'Manage'}
                </button>
              )}
            </div>
          </GlassCard>

          {usage && (
            <GlassCard style={{ padding: '24px 28px', animation: 'fadeUp 0.4s ease 0.15s both' }}>
              <div style={sectionTitleStyle}>Usage This Month</div>
              <UsageBar
                label="Voice calls"
                used={usage.voice.used}
                total={usage.voice.limit < 9999 ? usage.voice.limit : null}
                color={T.gold}
              />
              <div className="mb-4">
                <div
                  className="flex justify-between mb-2 text-[13px]"
                  style={{ color: T.cream60, fontFamily: T.sans }}
                >
                  <span>Emails sent</span>
                  <span className="font-mono text-xs" style={{ color: T.cream20 }}>
                    {usage.email.used}
                    {usage.email.limit >= 9999 ? '' : ` / ${usage.email.limit}`}
                  </span>
                </div>
                {usage.email.limit < 9999 && (
                  <div
                    className="h-[3px] rounded-[2px] overflow-hidden"
                    style={{ background: T.cream04 }}
                  >
                    <div
                      className="h-full rounded-[2px]"
                      style={{
                        background: '#e8c87c',
                        width: `${Math.min(100, (usage.email.used / usage.email.limit) * 100)}%`,
                        transition: 'width 1s cubic-bezier(0.4,0,0.2,1)',
                      }}
                    />
                  </div>
                )}
              </div>
            </GlassCard>
          )}

          <GlassCard style={{ padding: '24px 28px', animation: 'fadeUp 0.4s ease 0.2s both' }}>
            <div style={sectionTitleStyle}>Connections</div>

            <div
              className="flex justify-between items-center mb-4 pb-4"
              style={{ borderBottom: `1px solid ${T.cream04}` }}
            >
              <div>
                <div style={{ fontFamily: T.sans, fontSize: 14, fontWeight: 500, color: T.cream }}>
                  Phone
                </div>
                <div style={{ fontFamily: T.sans, fontSize: 12, color: T.cream35, marginTop: 2 }}>
                  {profile?.phoneVerified ? 'Verified' : 'Not verified'}
                </div>
              </div>
              {profile?.phoneVerified ? (
                <span
                  style={{
                    padding: '3px 10px',
                    borderRadius: 100,
                    background: T.greenDim,
                    color: T.green,
                    fontSize: 12,
                    fontWeight: 500,
                    fontFamily: T.sans,
                  }}
                >
                  Verified
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowPhoneVerify(true)}
                  style={{
                    padding: '7px 16px',
                    borderRadius: 8,
                    border: 'none',
                    background: T.gold,
                    color: T.bg,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: T.sans,
                  }}
                >
                  Verify phone
                </button>
              )}
            </div>

            <div className="flex justify-between items-center">
              <div>
                <div style={{ fontFamily: T.sans, fontSize: 14, fontWeight: 500, color: T.cream }}>
                  Gmail
                </div>
                <div style={{ fontFamily: T.sans, fontSize: 12, color: T.cream35, marginTop: 2 }}>
                  {profile?.gmailConnected ? 'Connected' : 'Not connected'}
                </div>
              </div>
              {profile?.gmailConnected ? (
                <span
                  style={{
                    padding: '3px 10px',
                    borderRadius: 100,
                    background: T.greenDim,
                    color: T.green,
                    fontSize: 12,
                    fontWeight: 500,
                    fontFamily: T.sans,
                  }}
                >
                  Connected
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    window.location.href = `${API_BASE_URL}/v1/auth/gmail`;
                  }}
                  style={{
                    padding: '7px 16px',
                    borderRadius: 8,
                    border: 'none',
                    background: T.amber,
                    color: T.bg,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: T.sans,
                  }}
                >
                  Connect Gmail
                </button>
              )}
            </div>
          </GlassCard>

          {tier === 'starter' && (
            <GlassCard style={{ padding: '24px 28px', animation: 'fadeUp 0.4s ease 0.25s both' }}>
              <div style={sectionTitleStyle}>Upgrade</div>
              <div style={{ fontFamily: T.sans, fontSize: 14, color: T.cream60, marginBottom: 20 }}>
                Upgrade to Pro or Max for more tasks.
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div
                  style={{
                    padding: '18px 20px',
                    borderRadius: 12,
                    border: `1px solid ${T.glassBorder}`,
                    background: T.cream04,
                  }}
                >
                  <div style={{ fontFamily: T.sans, fontWeight: 700, fontSize: 14, color: T.cream, marginBottom: 4 }}>
                    Pro
                  </div>
                  <div style={{ fontFamily: T.sans, fontSize: 12, color: T.cream35, marginBottom: 12 }}>
                    10 calls, 50 emails/month
                  </div>
                  <button
                    type="button"
                    onClick={() => handleUpgrade('pro')}
                    disabled={checkingOut !== null}
                    style={{
                      padding: '8px 16px',
                      borderRadius: 8,
                      border: 'none',
                      background: checkingOut === 'pro' ? T.cream20 : T.gold,
                      color: T.bg,
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: checkingOut ? 'default' : 'pointer',
                      fontFamily: T.sans,
                      width: '100%',
                    }}
                  >
                    {checkingOut === 'pro' ? 'Loading...' : 'Upgrade to Pro'}
                  </button>
                </div>

                <div
                  style={{
                    padding: '18px 20px',
                    borderRadius: 12,
                    border: `1px solid ${T.glassBorder}`,
                    background: T.cream04,
                  }}
                >
                  <div style={{ fontFamily: T.sans, fontWeight: 700, fontSize: 14, color: T.cream, marginBottom: 4 }}>
                    Max
                  </div>
                  <div style={{ fontFamily: T.sans, fontSize: 12, color: T.cream35, marginBottom: 12 }}>
                    50 calls, unlimited emails/month
                  </div>
                  <button
                    type="button"
                    onClick={() => handleUpgrade('max')}
                    disabled={checkingOut !== null}
                    style={{
                      padding: '8px 16px',
                      borderRadius: 8,
                      border: 'none',
                      background: checkingOut === 'max' ? T.cream20 : T.surface,
                      color: T.cream,
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: checkingOut ? 'default' : 'pointer',
                      fontFamily: T.sans,
                      width: '100%',
                      borderWidth: 1,
                      borderStyle: 'solid',
                      borderColor: T.cream12,
                    }}
                  >
                    {checkingOut === 'max' ? 'Loading...' : 'Upgrade to Max'}
                  </button>
                </div>
              </div>
            </GlassCard>
          )}
        </div>
      )}

      {showPhoneVerify && (
        <PhoneVerifyModal
          onClose={() => setShowPhoneVerify(false)}
          onVerified={() => {
            api<SubscriptionCurrentResponse>('/subscriptions/current')
              .then((data) => {
                setProfile(data.profile);
                setSub(data.subscription);
                setUsage(data.usage);
              })
              .catch(() => {});
          }}
        />
      )}

      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}
